import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";

// Excel-style column widths for any table: drag a header's right edge to resize it,
// double-click the edge to fit the column to its longest value. Not remembered — the
// widths live with the table instance and reset when its data or column set changes.
//
// The grab zone straddles the edge: half is the column's own right 6px, half is the
// next header's left 6px (a sticky header paints over anything poking out of its left
// neighbour, so one zone cannot overhang the line). A drag shows the column's span, its
// moving edge and its width on <body>; a one-time tip points first-time users at an edge.
//
// The first drag or double-click snapshots every column's CURRENT width into a
// <colgroup> and switches the table to fixed layout, so from then on resizing one
// column moves only that column (the table grows or shrinks, the rest stay put) and
// cells clip to it, as in Excel. Each column's width is a CSS variable on the <table>
// and the table's width is their sum, so a drag repaints by setting one variable —
// no row re-renders, which matters on the unpaginated grids. React state is written
// once, on release.

const MIN_W = 40;
// Finance's cap: a long narration can run past 1,500px. Longer values stay readable in
// the cell tooltip, and dragging by hand is not capped.
const AUTOFIT_MAX_W = 800;
// Off-page rows are ranked on a canvas; only the widest few are laid out for real,
// because canvas misses what the cell's CSS adds (chips, tabular figures, letter-spacing).
const PROBES = 8;
const GRIP_TITLE = "Drag to resize · double-click to fit";

// The tip shows, per browser, on the first table on screen with grips until the user
// acknowledges it: Got it, or any resize drag or double-click (they have found it).
const TIP_KEY = "cri_recon_colresize_tip";
const TIP_W = 248; // keep in sync with .cw-tip width
let tipAcked: boolean | null = null;
let tipOwner: object | null = null;
let hideTip: (() => void) | null = null;
const tipDone = () => (tipAcked ??= localStorage.getItem(TIP_KEY) === "1");
function ackTip() {
  if (!tipDone()) { tipAcked = true; localStorage.setItem(TIP_KEY, "1"); }
  hideTip?.();
}
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export interface ColumnWidths {
  /** Append to the table's className. */
  className: string;
  /** Merge into the table's style. */
  style: React.CSSProperties | undefined;
  /** Render as the table's first child. */
  colgroup: React.ReactNode;
  /** The resize grip, rendered inside a header cell. `texts` returns the column's text
   *  over every row the grid shows across all its pages; omit it where every row is
   *  rendered (autofit then reads the laid-out rows alone). */
  grip: (key: string, texts?: () => string[]) => React.ReactNode;
}

/**
 * @param keys      every column of the table, in render order (including columns with no
 *                  grip, such as a select column) — one <col> each
 * @param resetKey  data identity; a change drops the widths
 */
export function useColumnWidths(keys: string[], resetKey?: unknown): ColumnWidths {
  const sig = keys.join("\u0001");
  const [widths, setWidths] = useState<Record<string, number> | null>(null);
  const slots = useRef(new Map<string, number>());
  const keysRef = useRef(keys);
  keysRef.current = keys;

  // A different column SET (not order: a pin only reorders) drops the widths.
  const setSig = useMemo(() => [...keys].sort().join("\u0001"), [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setWidths(null); }, [resetKey, setSig]);

  const slot = useCallback((key: string) => {
    let n = slots.current.get(key);
    if (n === undefined) { n = slots.current.size; slots.current.set(key, n); }
    return `--cw${n}`;
  }, []);

  // Until the effect above clears a stale snapshot, render unsized rather than half-sized.
  // Same set in a new order (a pin) passes: each width travels with its column.
  const active = widths !== null && keys.every((k) => widths[k] !== undefined) ? widths : null;

  const style = useMemo(() => {
    if (!active) return undefined;
    const s: Record<string, string> = {};
    for (const k of keys) s[slot(k)] = `${active[k]}px`;
    s.width = `calc(${keys.map((k) => `var(${slot(k)})`).join(" + ")})`;
    return s as React.CSSProperties;
  }, [active, sig, slot]); // eslint-disable-line react-hooks/exhaustive-deps

  const colgroup = active ? (
    <colgroup>
      {keys.map((k) => <col key={k} style={{ width: `var(${slot(k)})` }} />)}
    </colgroup>
  ) : null;

  const activeRef = useRef(active);
  activeRef.current = active;
  const ensureSized = useCallback((table: HTMLTableElement) => {
    if (activeRef.current) return true;
    const ks = keysRef.current;
    const px = renderedWidths(table, ks.length);
    if (!px) return false;
    // Up, never to nearest: a column already exactly content-wide would lose its last
    // sub-pixel and grow an ellipsis.
    const w: Record<string, number> = {};
    ks.forEach((k, i) => { w[k] = Math.max(MIN_W, Math.ceil(px[i])); });
    flushSync(() => setWidths(w));
    return true;
  }, []);

  const commit = useCallback((key: string, px: number) => {
    setWidths((s) => (s && s[key] !== px ? { ...s, [key]: px } : s));
  }, []);

  // `grip` is whichever half was pressed; `th` is the column it resizes.
  const startDrag = useCallback((grip: HTMLElement, pointerId: number, x0: number, th: HTMLTableCellElement, key: string) => {
    const table = th.closest("table");
    if (!table || !ensureSized(table)) return;
    ackTip();
    const name = slot(key);
    const rect = th.getBoundingClientRect();
    const start = rect.width;
    let px = Math.round(start);
    grip.setPointerCapture(pointerId);
    document.body.classList.add("col-resizing");
    const feedback = dragFeedback(table, rect);
    feedback.show(px);
    const move = (ev: PointerEvent) => {
      px = Math.max(MIN_W, Math.round(start + ev.clientX - x0));
      table.style.setProperty(name, `${px}px`);
      feedback.show(px);
    };
    const end = () => {
      grip.removeEventListener("pointermove", move);
      grip.removeEventListener("pointerup", end);
      grip.removeEventListener("lostpointercapture", end);
      document.body.classList.remove("col-resizing");
      feedback.remove();
      commit(key, px);
    };
    grip.addEventListener("pointermove", move);
    grip.addEventListener("pointerup", end);
    grip.addEventListener("lostpointercapture", end);
  }, [ensureSized, slot, commit]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLSpanElement>, key: string) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest("th");
    if (th) startDrag(e.currentTarget, e.pointerId, e.clientX, th, key);
  }, [startDrag]);

  const onLeadDown = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const left = leftNeighbour(e.currentTarget);
    if (left) startDrag(e.currentTarget, e.pointerId, e.clientX, left.th, left.key);
  }, [startDrag]);

  const textsOf = useRef(new Map<string, (() => string[]) | undefined>());

  const autofit = useCallback((th: HTMLTableCellElement, key: string) => {
    const table = th.closest("table");
    if (!table || !ensureSized(table)) return;
    ackTip();
    const texts = textsOf.current.get(key);
    const name = slot(key);
    const host = table.parentElement;
    const scroll = host ? [host.scrollLeft, host.scrollTop] : null;
    const probes = texts ? addProbes(table, keysRef.current.indexOf(key), texts()) : [];
    // Auto layout at max-content, this column unsized: its header cell then measures the
    // widest thing in the column — header, every rendered row and the probes.
    table.style.setProperty(name, "auto");
    table.classList.add("cw-measuring");
    const natural = th.getBoundingClientRect().width;
    table.classList.remove("cw-measuring");
    probes.forEach((p) => p.remove());
    // +2: fixed layout rounds the column to whole pixels, and 1px short shows an ellipsis.
    const px = Math.min(AUTOFIT_MAX_W, Math.max(MIN_W, Math.ceil(natural) + 2));
    table.style.setProperty(name, `${px}px`);
    if (host && scroll) { host.scrollLeft = scroll[0]; host.scrollTop = scroll[1]; }
    commit(key, px);
  }, [ensureSized, slot, commit]);

  const onFit = useCallback((e: React.MouseEvent<HTMLSpanElement>, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest("th");
    if (th) autofit(th, key);
  }, [autofit]);

  const onLeadFit = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const left = leftNeighbour(e.currentTarget);
    if (left) autofit(left.th, left.key);
  }, [autofit]);

  // --- first-use tip: owned by one hook instance at a time (module-level tipOwner) ---
  const me = useRef({}).current;
  const gripEls = useRef(new Map<string, HTMLSpanElement>());
  const tipEl = useRef<HTMLDivElement>(null);
  const [tipKey, setTipKey] = useState<string | null>(null);
  const owned = tipKey !== null;

  // Written straight to the layer's style, so following the edge on scroll never
  // re-renders the grid. Hidden while the edge is off screen or scrolled out of its table.
  const placeTip = useCallback(() => {
    const el = tipEl.current;
    const g = tipKey === null ? undefined : gripEls.current.get(tipKey);
    if (!el) return;
    const r = g?.getBoundingClientRect();
    const host = g?.closest("table")?.parentElement?.getBoundingClientRect();
    const on = !!r && r.width > 0 && r.bottom > 0 && r.top < window.innerHeight
      && r.right > Math.max(0, host?.left ?? 0) && r.right <= Math.min(window.innerWidth, host?.right ?? Infinity);
    el.style.visibility = on ? "visible" : "hidden";
    if (!on || !r) return;
    const left = Math.max(8, Math.min(r.right - 24, window.innerWidth - TIP_W - 8));
    el.style.setProperty("--cw-x", `${r.right}px`);
    el.style.setProperty("--cw-mid", `${r.top + r.height / 2}px`);
    el.style.setProperty("--cw-left", `${left}px`);
    el.style.setProperty("--cw-top", `${r.bottom + 8}px`);
    el.style.setProperty("--cw-arrow", `${r.right - left - 5}px`);
  }, [tipKey]);

  // Every render: claim the tip once this table's grips are mounted, and keep it on the
  // first gripped column as the columns change.
  useEffect(() => {
    if (tipKey === null && (tipOwner !== null || tipDone())) return;
    const first = keysRef.current.find((k) => gripEls.current.has(k)) ?? null;
    if (tipKey === null) {
      if (first === null) return;
      tipOwner = me;
      setTipKey(first);
    } else if (first !== tipKey) setTipKey(first);
    else placeTip();
  });

  useEffect(() => {
    if (!owned) return;
    const hide = () => setTipKey(null);
    hideTip = hide;
    return () => {
      if (hideTip === hide) hideTip = null;
      if (tipOwner === me) tipOwner = null;
    };
  }, [owned, me]);

  useEffect(() => {
    if (!owned) return;
    window.addEventListener("scroll", placeTip, true);
    window.addEventListener("resize", placeTip);
    return () => {
      window.removeEventListener("scroll", placeTip, true);
      window.removeEventListener("resize", placeTip);
    };
  }, [owned, placeTip]);

  const grip = useCallback((key: string, texts?: () => string[]) => {
    textsOf.current.set(key, texts);
    return (
      <>
        <span className="col-grip col-grip--lead" aria-hidden="true" title={GRIP_TITLE}
          onPointerDown={onLeadDown} onClick={stop} onDoubleClick={onLeadFit} />
        <span className={`col-grip${key === tipKey ? " col-grip--tip" : ""}`} aria-hidden="true"
          title={GRIP_TITLE} data-cw-key={key}
          ref={(el) => { if (el) gripEls.current.set(key, el); else gripEls.current.delete(key); }}
          onPointerDown={(e) => onPointerDown(e, key)}
          onClick={stop}
          onDoubleClick={(e) => onFit(e, key)} />
        {key === tipKey && createPortal(
          // Portal events still bubble to the header cell: stop them before a sort or select.
          <div ref={tipEl} className="cw-tip-layer" onPointerDown={stop} onMouseDown={stop}
            onClick={stop} onDoubleClick={stop}>
            <span className="cw-tip-pulse" />
            <div className="cw-tip" role="note">
              <div>Drag a column edge to resize it.</div>
              <div className="cw-tip-row">
                <span className="cw-tip-sub">Double-click an edge to fit</span>
                <button type="button" className="cw-tip-btn" onClick={ackTip}>Got it</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }, [onPointerDown, onLeadDown, onFit, onLeadFit, tipKey]);

  return { className: active ? " cw-fixed" : "", style, colgroup, grip };
}

// The column a lead grip (the right half of an edge) resizes: the header cell to its
// left, when that cell has a grip and really is beside it (a rowSpan cell above or a
// pinned column scrolled over it makes DOM order lie).
function leftNeighbour(lead: HTMLElement): { th: HTMLTableCellElement; key: string } | null {
  const th = lead.closest("th");
  const prev = th?.previousElementSibling;
  if (!th || !(prev instanceof HTMLTableCellElement)) return null;
  const key = prev.querySelector<HTMLElement>(":scope > .col-grip[data-cw-key]")?.dataset.cwKey;
  if (key === undefined || Math.abs(prev.getBoundingClientRect().right - th.getBoundingClientRect().left) > 2) return null;
  return { th: prev, key };
}

// Drag feedback on <body>, so no grid's overflow or stacking can hide it, clipped to the
// part of the table on screen. The column's left edge holds still while it is dragged,
// so a move only writes styles — no layout read per pointermove.
function dragFeedback(table: HTMLTableElement, th: DOMRect) {
  const t = table.getBoundingClientRect();
  const host = table.parentElement?.getBoundingClientRect() ?? t;
  const top = Math.max(th.top, 0);
  const height = Math.max(0, Math.min(t.bottom, host.bottom, window.innerHeight) - top);
  const clipL = Math.max(host.left, 0);
  const clipR = Math.min(host.right, window.innerWidth);
  const add = (cls: string) => {
    const d = document.createElement("div");
    d.className = cls;
    document.body.appendChild(d);
    return d;
  };
  const tint = add("cw-tint");
  const guide = add("cw-guide");
  const badge = add("cw-badge");
  for (const d of [tint, guide]) { d.style.top = `${top}px`; d.style.height = `${height}px`; }
  badge.style.top = `${th.bottom + 6}px`;
  return {
    show(px: number) {
      const edge = th.left + px;
      const l = Math.max(th.left, clipL);
      tint.style.left = `${l}px`;
      tint.style.width = `${Math.max(0, Math.min(edge, clipR) - l)}px`;
      guide.style.left = `${edge}px`;
      guide.hidden = edge < clipL || edge > clipR;
      badge.textContent = `${px} px`;
      badge.style.left = `${Math.min(edge, clipR)}px`;
      // Near the right edge the badge flips to the line's left rather than run off screen.
      badge.classList.toggle("cw-badge--flip", edge + 80 > clipR);
    },
    remove() { tint.remove(); guide.remove(); badge.remove(); },
  };
}

// Each column's rendered width, read off the first single-span cell that lands in it.
// Walks the rows as the table grid does, so banner rows (colSpan) and a header cell
// spanning both header rows (rowSpan) still resolve every column.
function renderedWidths(table: HTMLTableElement, n: number): number[] | null {
  const w: number[] = [];
  const covered = new Array<number>(n).fill(0); // rows a rowSpan still holds, per column
  let found = 0;
  for (const row of Array.from(table.rows)) {
    let col = 0;
    for (const cell of Array.from(row.cells)) {
      while (col < n && covered[col] > 0) col++;
      if (col >= n) break;
      if (cell.colSpan === 1 && w[col] === undefined) { w[col] = cell.getBoundingClientRect().width; found++; }
      for (let i = col; i < Math.min(n, col + cell.colSpan); i++) covered[i] = Math.max(1, cell.rowSpan);
      col += cell.colSpan;
    }
    for (let i = 0; i < n; i++) if (covered[i] > 0) covered[i]--;
    if (found === n) return w;
  }
  return null;
}

let canvas: HTMLCanvasElement | null = null;

// Temporary rows carrying the column's widest off-page values, laid out as clones of a
// rendered cell of that column (same classes, so the same padding, chips and font).
// Appended and removed inside one synchronous handler, so React never sees them.
function addProbes(table: HTMLTableElement, col: number, texts: string[]): HTMLTableRowElement[] {
  const body = table.tBodies[0];
  const template = col >= 0 && body ? Array.from(body.rows).find((r) =>
    r.cells.length > col && Array.from(r.cells).every((c) => c.colSpan === 1)) : undefined;
  if (!template) return [];
  const cell = template.cells[col];
  const cs = getComputedStyle(cell);
  canvas ??= document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const seen = new Set<string>();
  const top: { t: string; w: number }[] = [];
  for (const t of texts) {
    if (!t || seen.has(t)) continue;
    seen.add(t);
    const w = ctx.measureText(t).width;
    if (top.length === PROBES && w <= top[PROBES - 1].w) continue;
    top.push({ t, w });
    top.sort((a, b) => b.w - a.w);
    if (top.length > PROBES) top.pop();
  }
  return top.map(({ t }) => {
    const tr = document.createElement("tr");
    for (let i = 0; i < col; i++) tr.appendChild(document.createElement("td"));
    const c = cell.cloneNode(true) as HTMLTableCellElement;
    setText(c, t);
    tr.appendChild(c);
    body.appendChild(tr);
    return tr;
  });
}

// Swap the text, keep the markup (a chip span keeps its padding).
function setText(cell: HTMLElement, text: string) {
  const walk = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
  let first: Text | null = null;
  for (let n = walk.nextNode(); n; n = walk.nextNode()) {
    if (!first) { first = n as Text; first.data = text; } else (n as Text).data = "";
  }
  if (!first) cell.textContent = text;
}
